import { z } from 'zod';

/**
 * Asset type enumeration
 */
export enum AssetType {
  CASE_CART = 'CASE_CART',
  TRAY = 'TRAY',
  EQUIPMENT = 'EQUIPMENT',
  STRETCHER = 'STRETCHER',
  IMPLANT = 'IMPLANT',
}

export const AssetTypeSchema = z.nativeEnum(AssetType);

/**
 * Asset Tray type enumeration
 */
export enum TrayType {
  HOSPITAL = 'HOSPITAL',
  OEM = 'OEM',
}

export const TrayTypeSchema = z.nativeEnum(TrayType);

/**
 * Asset status enumeration
 */
export const AssetStatusSchema = z.union([
  z.literal('AVAILABLE'),
  z.literal('ALLOCATED'),
  z.literal('RESERVED'),
  z.literal('ASSEMBLED'),
  z.literal('IN_ASSEMBLY'),
  z.literal('UNAVAILABLE'),
  z.literal('OFF_RACK'),
  z.literal('CHECK_IN'),
  z.literal('CHECKING_IN'),
  z.literal('CHECKED_IN'),
  z.literal('DECON'),
]);
export type AssetStatus = z.infer<typeof AssetStatusSchema>;

/**
 * Asset description object
 */
export const AssetDescriptionSchema = z.object({
  /** Asset description text */
  description: z.string().nullable(),
});
export type AssetDescription = z.infer<typeof AssetDescriptionSchema>;

/**
 * Asset location object. Unknown server-supplied keys are preserved
 * (the legacy interface declared an open `[key: string]: unknown`).
 */
export const AssetLocationSchema = z
  .object({
    /** Location identifier */
    id: z.string().optional(),
    /** Location name */
    name: z.string().optional(),
    departmentOrUnit: z.string().optional(),
    floor: z.string().optional(),
  })
  .catchall(z.unknown());
export type AssetLocation = z.infer<typeof AssetLocationSchema>;

/**
 * Tray count sheet details (only for TRAY assets)
 */
export const TrayCountSheetSchema = z.object({
  /** Count sheet ID */
  id: z.string(),
  /** Hospital tray identifier this count sheet represents */
  trayId: z.string(),
  /** Tray name */
  trayName: z.string(),
  /** ISO8601 timestamp when the count sheet was created */
  createdAt: z.string(),
  /** ISO8601 timestamp when the count sheet was last updated */
  updatedAt: z.string(),
  /** Instruments */
  instruments: z.array(z.unknown()),
});
export type TrayCountSheet = z.infer<typeof TrayCountSheetSchema>;

/**
 * Tray details (only for TRAY assets)
 */
export const TrayDetailsSchema = z.object({
  /** Tray count sheet details */
  countSheet: TrayCountSheetSchema,
  /** Tray type */
  trayType: TrayTypeSchema,
});

export type TrayDetails = z.infer<typeof TrayDetailsSchema>;

/**
 * Asset response DTO (Data Transfer Object) for API communication
 * This represents the raw data structure from the API
 */
export const AssetResponseDtoSchema = z.object({
  /** Unique asset identifier */
  id: z.string(),
  /** Asset name */
  name: z.string(),
  /** Asset description object */
  description: z.string().nullable(),
  /** Type of asset */
  type: AssetTypeSchema,
  /** Current status of the asset */
  status: AssetStatusSchema.nullable(),
  /** Asset manufacturer */
  manufacturer: z.string(),
  /** Physical asset reference number or barcode */
  referenceNumber: z.string(),
  /** Asset creation timestamp (ISO date-time string) */
  createdAt: z.string(),
  /** Asset catalog item ID */
  assetCatalogId: z.string(),
  /** Last time the asset was detected at its current location */
  locationUpdatedAt: z.string().nullish(),
  /** Tray details including count sheet (only for TRAY assets) */
  trayDetails: TrayDetailsSchema.nullish(),
  /** Current location */
  location: AssetLocationSchema.nullish(),
  /** Display name for location (e.g. "Parent - Child") for list and timeline */
  locationDisplayName: z.string().nullish(),
  /** External case identifier currently assigned to the asset */
  caseId: z.string().nullish(),
});
export type AssetResponseDto = z.infer<typeof AssetResponseDtoSchema>;

/**
 * Filter parameters for fetching assets
 */
export interface AssetFilterParams {
  /** Filter assets by type (supports comma-separated values for multiple types) */
  type?: AssetType | string;
  /** Search string for assets (case-insensitive) */
  search?: string;
  /** Filter assets whose catalog name contains this value (case-insensitive) */
  name?: string;
  /** Filter assets whose current location matches this ID */
  locationId?: string;
  /** Filter assets whose catalog item id matches this value */
  assetCatalogId?: string;
  /** Filter assets by status */
  status?: AssetStatus;
  /** When true, each asset includes its currently assigned external case identifier (caseId). Disabled by default to keep the standard list query lean. */
  includeAssignedCase?: boolean;
  /** Page number (1-based for API, 0-based for DataGrid) */
  page?: number;
  /** Number of items per page */
  pageSize?: number;
}

/**
 * Paginated response from the assets API
 */
export const PaginatedAssetResponseDtoSchema = z.object({
  /** Array of assets in the current page */
  data: z.array(AssetResponseDtoSchema),
  /** Total number of assets matching the query */
  total: z.number(),
  /** Current page number (1-indexed) */
  page: z.number(),
  /** Number of items per page */
  limit: z.number(),
});
export type PaginatedAssetResponseDto = z.infer<
  typeof PaginatedAssetResponseDtoSchema
>;

/**
 * Asset history event DTO
 */
export const AssetHistoryEventDtoSchema = z.object({
  /** Unique identifier of the history entry */
  id: z.string(),
  /** When the event was recorded */
  recordedAt: z.string(),
  /** When this history row was last observed at this location/status snapshot */
  lastSeen: z.string(),
  /** Snapshot of location name at record time */
  locationName: z.string().nullish(),
  /** Display name for location (e.g. "Parent - Child") */
  locationDisplayName: z.string().nullish(),
  /** Asset status at record time */
  status: AssetStatusSchema.nullish(),
  /** External case ID when asset was assigned to a case */
  caseId: z.string().nullish(),
});
export type AssetHistoryEventDto = z.infer<typeof AssetHistoryEventDtoSchema>;

/**
 * Create asset request DTO
 */
export interface CreateAssetDto {
  catalogItemId: string;
  referenceNumber: string;
  name?: string;
  status?: AssetStatus;
}

/**
 * Update asset request DTO
 */
export interface UpdateAssetDto {
  status?: AssetStatus;
  referenceNumber?: string;
}

/**
 * Asset history query params
 */
export interface AssetHistoryQueryParams {
  /** Start of date range (ISO 8601) */
  startDate: string;
  /** End of date range (ISO 8601) */
  endDate: string;
  /** Page number (1-based) */
  page?: number;
  /** Page size */
  limit?: number;
}

/**
 * Paginated response from the asset history API
 */
export const PaginatedAssetHistoryResponseDtoSchema = z.object({
  /** Array of history events in the current page */
  data: z.array(AssetHistoryEventDtoSchema),
  /** Total number of history events in the date range */
  total: z.number(),
  /** Current page number (1-indexed) */
  page: z.number(),
  /** Number of items per page */
  limit: z.number(),
});
export type PaginatedAssetHistoryResponseDto = z.infer<
  typeof PaginatedAssetHistoryResponseDtoSchema
>;
